import { createContext, useRef, useState } from 'react';
import { voiceParseAPI } from '../redux/slices/app/chatBotSlice';
import { useDispatch } from 'react-redux';
import { axiosRelayInstance } from '../../axios';

const VoiceContext = createContext(null);

export const VoiceProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isListening, setIsListening] = useState(false);
  const [action, setAction] = useState({ id: '', command: '', params: {} });

  const websocketRef = useRef(null); // Store WebSocket reference
  const microphoneRef = useRef(null); // Store Microphone reference

  const actionExecuted = async (executeAction, stopMic = true) => {
    setAction({ id: '', command: '', params: {} });
    if (stopMic) {
      await listen(false);
    }
  };

  const listen = async listenState => {
    if (listenState) {
      const mic = await getMicrophone();
      await openMicrophone(mic);
    } else {
      await closeMicrophone();
    }
  };

  let fullTranscriptData = '';

  const callParseApi = async transcript => {
    const response = await dispatch(voiceParseAPI({ source: 'VOICE', user_input: transcript }));
    if (response.payload.status !== 200)
      throw new Error(`Invalid Response Status ${response.payload.status}`);
    return response.payload.data;
  };

  let lastParseCallTime = 0;
  let parseTimeout = null;

  const handleTranscriptV2 = async transcriptData => {
    fullTranscriptData += transcriptData.transcript;
    if (parseTimeout) {
      clearTimeout(parseTimeout);
    }

    const callParse = async triggeredByPunctuation => {
      lastParseCallTime = Date.now();
      const parseResult = await callParseApi(fullTranscriptData);

      if (parseResult?.response) {
        try {
          const parsedResponse = parseResult?.response;
          if (parsedResponse?.Command) {
            setAction(prevAction => {
              if (
                prevAction.command === parsedResponse.Command &&
                JSON.stringify(prevAction.params) === JSON.stringify(parsedResponse.Parameters)
              ) {
                return prevAction; // Avoid setting state if nothing has changed
              }
              return {
                id: crypto.randomUUID(),
                command: parsedResponse.Command,
                params: parsedResponse.Parameters
              };
            });

            // Reset fullTranscriptData only if a command is found
            fullTranscriptData = '';
          }
        } catch (e) {
          console.error('Error in parsing voice JSON', e);
        }
      }
      // Reset fullTranscriptData only if triggered by punctuation
      if (triggeredByPunctuation) {
        fullTranscriptData = '';
      }
    };

    if (transcriptData.endsWithPunctuation) {
      await callParse(true);
    } else {
      parseTimeout = setTimeout(() => {
        if (Date.now() - lastParseCallTime >= 2000) {
          callParse(false);
        }
      }, 2000);
    }
  };

  const configureWebsocket = websocket => {
    // websocket.onopen = () => console.log('Connected to server');
    websocket.onmessage = async event => await handleTranscriptV2(JSON.parse(event.data));
    websocket.onclose = () => closeMicrophone();
    websocket.onerror = error => console.error('WebSocketError: ', error);
  };

  const sendMessageToServer = async (message, shouldLog = true) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      websocketRef.current.send(message);
      //   if (shouldLog) console.log(`Message to server: ${message}`);
    }
  };

  const getMicrophone = async () => {
    if (!microphoneRef.current) {
      const userMedia = await navigator.mediaDevices.getUserMedia({ audio: true });
      microphoneRef.current = new MediaRecorder(userMedia);
    }
    return microphoneRef.current;
  };

  const openMicrophone = async mic => {
    if (!mic) return;

    mic.onstart = () => setIsListening(true);
    mic.onstop = () => setIsListening(false);

    websocketRef.current = new WebSocket(
      `${process.env.REACT_RELAY_API_URL.replace('https://', 'wss://')}?token=${axiosRelayInstance.defaults.headers.common.Authorization}`
    );
    configureWebsocket(websocketRef.current);

    mic.ondataavailable = async e => {
      if (e.data.size > 0) await sendMessageToServer(e.data, true);
    };

    mic.start(1000);
  };

  const closeMicrophone = async () => {
    if (microphoneRef.current) {
      microphoneRef.current.stop();
      microphoneRef.current.stream.getTracks().forEach(track => track.stop());
      microphoneRef.current = null;
    }
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    setIsListening(false);
  };

  return (
    <VoiceContext.Provider value={{ isListening, listen, action, actionExecuted }}>
      {children}
    </VoiceContext.Provider>
  );
};

export default VoiceContext;
